import React, { useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import styles from './RichTextEditorWithCharCount.module.css'

const MAX_CHAR_COUNT = 190

const RichTextEditorWithCharCount = ({ onChange, value, label, className }) => {
  const quillRef = useRef()
  const previousValueRef = useRef(value || '')

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          ['link']
        ]
      },
      clipboard: {
        matchVisual: false
      }
    }),
    []
  )

  const formats = [
    'bold',
    'italic',
    'underline',
    'link'
  ]

  const getTextLength = (html) => {
    if (!html) return 0
    // Create a temporary div to extract text content (excluding HTML tags)
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    const textContent = tempDiv.textContent || tempDiv.innerText || ''
    return textContent.length
  }

  // Update previous value ref when value prop changes
  useEffect(() => {
    previousValueRef.current = value || ''
  }, [value])

  const handleChange = (content) => {
    if (onChange) {
      // Get plain text length
      const textLength = getTextLength(content)
      const previousLength = getTextLength(previousValueRef.current)

      // If the new content would exceed limit (over 190) and is longer than previous, prevent the change
      // This prevents new input while preserving existing content that may already be over limit
      // Allow exactly 190 characters (textLength === MAX_CHAR_COUNT)
      if (textLength > MAX_CHAR_COUNT && textLength > previousLength) {
        // Revert to previous value to prevent new input
        if (quillRef.current) {
          const editor = quillRef.current.getEditor()
          setTimeout(() => {
            editor.setContents(previousValueRef.current)
          }, 0)
        }
        // Don't call onChange - keep the previous value
        return
      }

      // Always allow onChange - preserves existing content even if over limit, and allows exactly 190
      previousValueRef.current = content
      onChange(content)
    }
  }

  // Prevent keyboard input when at limit
  useEffect(() => {
    if (!quillRef.current) return

    const editor = quillRef.current.getEditor()
    const editorElement = editor.root

    const handleKeyDown = (e) => {
      // Get current HTML content directly from editor to get real-time length
      // editor.root is the contentEditable div with the actual content
      const currentHTML = editor.root.innerHTML || ''
      // Use the same getTextLength method as handleChange for consistency
      const currentLength = getTextLength(currentHTML)

      // Allow backspace, delete, arrow keys, navigation keys, etc.
      const allowedKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
        'ArrowUp', 'ArrowDown', 'Home', 'End',
        'Tab', 'Enter', 'Escape'
      ]

      if (allowedKeys.includes(e.key)) {
        return // Allow these keys
      }

      // Allow Ctrl/Cmd combinations (for shortcuts like Ctrl+A, Ctrl+C, Ctrl+V, etc.)
      if (e.ctrlKey || e.metaKey) {
        return // Allow shortcuts
      }

      // Check if this is a character input
      const isCharacterInput = (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) || e.key === 'Space'

      if (isCharacterInput) {
        // Get selection to check if text will be replaced or inserted
        const selection = editor.getSelection()
        let wouldBeLength = currentLength

        if (selection && selection.length > 0) {
          // Text is selected, it will be replaced
          // Get selected text and calculate its length using the same method
          const selectedText = editor.getText(selection.index, selection.length)
          // For HTML content, we need to get the HTML of the selection
          // Use a simpler approach: get the delta and estimate, or use text length
          // Since we're comparing with getTextLength which strips HTML, use text length
          const selectedLength = selectedText.length
          wouldBeLength = currentLength - selectedLength + 1
        } else {
          // Text will be inserted
          wouldBeLength = currentLength + 1
        }

        // Prevent input only if it would exceed 190 characters (allow exactly 190)
        if (wouldBeLength > MAX_CHAR_COUNT) {
          e.preventDefault()
          e.stopPropagation()
          return false
        }
      }
    }

    const handlePaste = (e) => {
      const currentText = editor.getText()

      // Get pasted text
      const pastedText = (e.clipboardData || window.clipboardData).getData('text/plain')

      // Calculate what the length would be after paste
      const selection = editor.getSelection()
      let textAfterPaste = currentText

      if (selection && selection.length > 0) {
        // If text is selected, it will be replaced
        textAfterPaste = currentText.slice(0, selection.index) +
                        pastedText +
                        currentText.slice(selection.index + selection.length)
      } else {
        // Text will be inserted
        textAfterPaste = currentText.slice(0, selection?.index || 0) +
                        pastedText +
                        currentText.slice(selection?.index || 0)
      }

      // Prevent paste if it would exceed limit
      if (textAfterPaste.length > MAX_CHAR_COUNT) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }

    editorElement.addEventListener('keydown', handleKeyDown)
    editorElement.addEventListener('paste', handlePaste)

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown)
      editorElement.removeEventListener('paste', handlePaste)
    }
  }, [value])

  const currentCharCount = getTextLength(value || '')
  const isOverLimit = currentCharCount > MAX_CHAR_COUNT

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.editorContainer}>
        <ReactQuill
          ref={quillRef}
          className={styles.editor}
          formats={formats}
          modules={modules}
          onChange={handleChange}
          theme='snow'
          value={value || ''}
        />
        <div className={`${styles.charCount} ${isOverLimit ? styles.charCountError : ''}`}>
          {currentCharCount} / {MAX_CHAR_COUNT}
        </div>
      </div>
    </div>
  )
}

RichTextEditorWithCharCount.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string
}

RichTextEditorWithCharCount.defaultProps = {
  value: '',
  label: '',
  className: ''
}

export default RichTextEditorWithCharCount
